import React from "react";
import * as M from "../BlankModal";
import * as H from "../../../hooks";
import * as C from "../../../Common";
import { TB, TC, T } from "../../../Constants";

export type SelectProps = Pick<M.StyleModalProps, "title" | "onQuit" | "size"> & {
    /** Label of the select input */
    label?: string;
    /** List of options to offer to select */
    options?: T.Option[];
    /** Default value selected in the select */
    defaultVal?: string;
    /** Is a value required for validation */
    isRequired?: boolean;
    /** Text content of the confirm button */
    confirmText?: string;
    /** Text content of the description */
    description?: string;
    /** Do not show an error message if no options were found */
    no_error_if_empty?: boolean;
    /** Text content of the 'No selection' button (if not required) */
    noSelectionText?: string;
    /** Extra button to display in the footer */
    extraButton?: React.ReactNode;
    /** Extra props to pass on to the select input */
    selectProps?: Omit<C.TypeAheadProps, "options">;
    /** Callback for validating the selection */
    onValidate?: (selection?: string) => void;
    /** Extra props to offer a filter for the available options */
    filter?: {
        /** A function callback to filter each option */
        filter: (option: T.Option, selected_filter: string) => boolean;
        /** A list of filter options */
        options: T.Option[];
        /** The default value of the filter */
        value?: string;
        /** A label for the filter component */
        label?: string;
    }
}

const ModalSelect: React.FC<SelectProps> = ({ onValidate, onQuit, ...props }) => {
    const lg = H.useLanguage();
    const [selection, setSelection] = React.useState<string>();
    const [filter, set_filter] = React.useState(props.filter?.value);

    const validOptions = React.useMemo(() => {
        if (!Array.isArray(props.options)) return [];
        let options = props.options.filter(({ label, value }) => [label, value].every(TB.validString));
        if (typeof props.filter?.filter === "function" && filter) options = options.filter(o => props.filter.filter(o, filter));
        if (!props.isRequired) options.push({ label: "", value: "" });
        return options;
    }, [props.options, props.isRequired, props.filter, filter]);

    React.useEffect(() => {
        const values = validOptions.map(o => o.value);
        if (TB.validString(props.defaultVal) && values.includes(props.defaultVal)) setSelection(props.defaultVal);
        else if (props.isRequired && validOptions.length > 0) setSelection(validOptions[0].value);
    }, [validOptions, props.isRequired, props.defaultVal]);

    const body = React.useMemo(() => <div>

        {props.filter && <C.Form.Select
            value={filter}
            options={props.filter.options}
            onChange={value => set_filter(value)}
            label={props.filter.label || TC.SELECT_MODAL_FILTER_BY}
        />}

        {validOptions.length > 0 || props.no_error_if_empty ?
            <C.Form.Select
                value={selection}
                label={props.label}
                options={validOptions}
                typeahead={props.selectProps}
                description={props.description}
                onChange={value => setSelection(value)}
            />
            : <div className="alert alert-danger alert-imaged">
                {lg.getStaticText(TC.GLOBAL_NO_OPTIONS_SELECT)}
                <i className="fa fa-exclamation-triangle"></i>
            </div>}
    </div>, [validOptions, selection, props.label, props.description, props.selectProps, lg, props.no_error_if_empty]);

    const footer = React.useMemo(() => <C.Flex justifyContent="end">
        {props.extraButton}

        {!props.isRequired && <C.Button
            variant="secondary"
            onClick={() => onValidate?.()}
            title={props.noSelectionText || TC.GLOBAL_NO_SELECTION}
        />}

        <C.Button
            className="ms-2"
            disabled={validOptions.length === 0}
            onClick={() => onValidate?.(selection)}
            text={props.confirmText || TC.GLOBAL_CONFIRM}
        />
    </C.Flex>, [selection, validOptions, onValidate, props.confirmText, props.noSelectionText, props.isRequired, props.extraButton]);

    return <M.default
        children={body}
        onQuit={onQuit}
        footer={footer}
        size={props.size}
        title={props.title || TC.GLOBAL_SELECT}
    />;
}

export default ModalSelect;